import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import ContactPicker from '../components/ContactPicker';
import {STATE_LABELS} from '../constants/address';
import {api} from '../../api';

const ACTIVITY_TYPES = ['CALL', 'EMAIL', 'MEETING', 'LINKEDIN_MESSAGE'];
const PRIORITY_COLORS = { HIGH: 'bg-red-100 text-red-800', MEDIUM: 'bg-yellow-100 text-yellow-800', LOW: 'bg-green-100 text-green-800' };
const STATUS_COLORS = { TODO: 'bg-gray-100 text-gray-800', IN_PROGRESS: 'bg-blue-100 text-blue-800', DONE: 'bg-green-100 text-green-800' };

function ActivityForm({ initial, initialContacts, currentContactId, currentContactName, onSubmit, onCancel, submitLabel }) {
  const [form, setForm] = useState(initial);
  const [selectedContacts, setSelectedContacts] = useState(initialContacts);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return;
    const contactIds = selectedContacts.map(c => c.id);
    if (!contactIds.includes(currentContactId)) contactIds.push(currentContactId);
    const payload = { ...form, contactIds };
    if (payload.activityDate) {
      payload.activityDate = new Date(payload.activityDate).toISOString();
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="mb-4 space-y-3 border-b pb-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600">
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').charAt(0) + t.replace(/_/g, ' ').slice(1).toLowerCase()}</option>)}
        </select>
        <input type="text" placeholder="Subject *" required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
      </div>
      <div className="flex gap-2">
        <input type="datetime-local" value={form.activityDate} onChange={(e) => setForm({...form, activityDate: e.target.value})} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
      </div>
      <textarea placeholder="Details (optional)" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
      <div>
        <p className="text-sm text-gray-500 mb-1">With <span className="text-gray-400">({currentContactName} + others)</span></p>
        <ContactPicker selected={selectedContacts.filter(c => c.id !== currentContactId)} onChange={(contacts) => setSelectedContacts(contacts)} />
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>}
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{submitLabel}</button>
      </div>
    </form>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [assignedSubtasks, setAssignedSubtasks] = useState([]);
  const [showCompletedSubtasks, setShowCompletedSubtasks] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [userMap, setUserMap] = useState({});

  const loadContact = () => api(`/contacts/${id}`).then(r => r.json()).then(setContact);
  const loadActivities = () => api(`/crm-activities?contactId=${id}`).then(r => r.json()).then(setActivities);
  const loadTasks = () => api(`/crm-tasks?contactId=${id}`).then(r => r.json()).then(setTasks);

  useEffect(() => {
    loadContact();
    loadActivities();
    loadTasks();
    api(`/deals`).then(r => r.json()).then(all => setDeals(all.filter(d => d.contacts && d.contacts.some(c => c.id === id)))).catch(() => {});
    api('/tenant-users').then(r => r.json()).then(users => {
      const map = {};
      (users || []).forEach(u => { map[u.userId] = `${u.firstName} ${u.lastName}`.trim() || u.username; });
      setUserMap(map);
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    let current = true;
    api(`/contacts/${id}/crm-subtasks${showCompletedSubtasks ? '' : '?completed=false'}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (current) setAssignedSubtasks(data); })
      .catch(() => {});
    return () => { current = false; };
  }, [id, showCompletedSubtasks]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    const body = {
      firstName: contact.firstName, lastName: contact.lastName, status: contact.status,
      emails: contact.emails, phones: contact.phones, addresses: contact.addresses,
      notes: [...(contact.notes || []), { body: newNote.trim() }],
      companies: (contact.companies || []).map(c => ({ companyId: c.companyId, title: c.title, emails: c.emails, phones: c.phones })),
      tagIds: (contact.tags || []).map(t => t.id),
      sourceId: contact.sourceId || null
    };
    const res = await api(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (res.ok) { setNewNote(''); loadContact(); }
  };

  const handleAddActivity = async (form) => {
    const res = await api(`/crm-activities`, { method: 'POST', body: JSON.stringify(form) });
    if (res.ok) { setShowActivityForm(false); loadActivities(); }
  };

  const handleUpdateActivity = async (form) => {
    const res = await api(`/crm-activities/${editingActivityId}`, { method: 'PUT', body: JSON.stringify(form) });
    if (res.ok) { setEditingActivityId(null); loadActivities(); }
  };

  const handleDelete = async () => {
    await api(`/contacts/${id}`, { method: 'DELETE' });
    navigate('/crm/contacts');
  };

  if (!contact) return null;

  const currentContactName = `${contact.firstName} ${contact.lastName}`;

  return (
    <Layout>
      <Link to="/crm/contacts" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Contacts</Link>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{currentContactName}</h1>
              {contact.companies && contact.companies.length > 0 && (
                <p className="text-gray-600">
                  {contact.companies.map((co, i) => (
                    <span key={co.companyId}>
                      {i > 0 && ' · '}
                      {co.title && <span className="text-gray-500">{co.title} at </span>}
                      <Link to={`/crm/companies/${co.companyId}`} className="text-blue-600 hover:text-blue-800">{co.companyName}</Link>
                    </span>
                  ))}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                contact.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {contact.status}
              </span>
              {contact.tags && contact.tags.length > 0 && contact.tags.map(t => (
                <span key={t.id} className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">{t.name}</span>
              ))}
              <Link to={`/crm/contacts/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-blue-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Emails</p>
            <div className="text-gray-900 font-medium">
              {contact.emails && contact.emails.length > 0
                ? contact.emails.map((e, i) => <div key={i}>{e.email} <span className="text-gray-400 text-sm">({e.type})</span></div>)
                : '—'}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Phones</p>
            <div className="text-gray-900 font-medium">
              {contact.phones && contact.phones.length > 0
                ? contact.phones.map((p, i) => <div key={i}>{p.phone} <span className="text-gray-400 text-sm">({p.type})</span></div>)
                : '—'}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Addresses</p>
            <div className="text-gray-900 font-medium">
              {contact.addresses && contact.addresses.length > 0
                ? contact.addresses.map((a, i) => (
                    <div key={i} className="mb-2">
                      <span className="text-gray-400 text-xs uppercase">{a.type}</span>
                      {a.line1 && <div>{a.line1}</div>}
                      {a.line2 && <div>{a.line2}</div>}
                      {a.line3 && <div>{a.line3}</div>}
                      <div>{[a.city, a.state ? (STATE_LABELS[a.state] || a.state) : null, a.zip].filter(Boolean).join(', ')}</div>
                      {a.country && <div className="text-gray-500 text-sm">{a.country}</div>}
                    </div>
                  ))
                : '—'}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Source</p>
            <div className="text-gray-900 font-medium">{contact.sourceName || '—'}</div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Companies</p>
            <div className="text-gray-900 font-medium">
              {contact.companies && contact.companies.length > 0
                ? contact.companies.map(co => (
                    <div key={co.companyId} className="mb-2">
                      <Link to={`/crm/companies/${co.companyId}`} className="text-blue-600 hover:text-blue-800">{co.companyName}</Link>
                      {co.title && <span className="text-gray-500 text-sm ml-1">({co.title})</span>}
                      {co.emails && co.emails.length > 0 && <div className="text-sm text-gray-600">{co.emails.join(', ')}</div>}
                      {co.phones && co.phones.length > 0 && <div className="text-sm text-gray-600">{co.phones.join(', ')}</div>}
                    </div>
                  ))
                : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Activity</h2>
          <button onClick={() => { setShowActivityForm(!showActivityForm); setEditingActivityId(null); }} className="text-sm text-blue-600 hover:text-blue-800">
            {showActivityForm ? 'Cancel' : '+ Log activity'}
          </button>
        </div>
        <div className="p-4 md:p-6">
          {showActivityForm && (
            <ActivityForm
              initial={{ type: 'CALL', subject: '', body: '', activityDate: new Date().toISOString().slice(0, 16) }}
              initialContacts={[]}
              currentContactId={id}
              currentContactName={currentContactName}
              onSubmit={handleAddActivity}
              onCancel={() => setShowActivityForm(false)}
              submitLabel="Log"
            />
          )}
          {activities.length > 0 ? (
            <div className="space-y-4">
              {activities.map((a) => (
                <div key={a.id} className="border-b pb-3 last:border-b-0">
                  {editingActivityId === a.id ? (
                    <ActivityForm
                      initial={{ type: a.type, subject: a.subject, body: a.body || '', activityDate: a.activityDate ? a.activityDate.slice(0, 16) : '', dealId: a.dealId || null }}
                      initialContacts={(a.contacts || []).map(c => ({ id: c.id, firstName: c.firstName, lastName: c.lastName }))}
                      currentContactId={id}
                      currentContactName={currentContactName}
                      onSubmit={handleUpdateActivity}
                      onCancel={() => setEditingActivityId(null)}
                      submitLabel="Save"
                    />
                  ) : (
                    <div className="flex gap-3">
                      <span className={`mt-1 px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                        a.type === 'CALL' ? 'bg-blue-100 text-blue-800' : a.type === 'EMAIL' ? 'bg-purple-100 text-purple-800' : a.type === 'LINKEDIN_MESSAGE' ? 'bg-sky-100 text-sky-800' : 'bg-orange-100 text-orange-800'
                      }`}>{a.type}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-gray-900 font-medium">{a.subject}</p>
                        {a.dealName && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            Deal: <Link to={`/crm/deals/${a.dealId}`} className="text-blue-600 hover:text-blue-800">{a.dealName}</Link>
                          </p>
                        )}
                        {a.contacts && a.contacts.length > 0 && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            With {a.contacts.map((c, i) => (
                              <span key={c.id}>
                                {i > 0 && ', '}
                                {c.id === id ? <span>{c.firstName} {c.lastName}</span> : <Link to={`/crm/contacts/${c.id}`} className="text-blue-600 hover:text-blue-800">{c.firstName} {c.lastName}</Link>}
                              </span>
                            ))}
                          </p>
                        )}
                        {a.performedBy && <p className="text-sm text-gray-500 mt-0.5">By {userMap[a.performedBy] || a.performedBy}</p>}
                        {a.body && <p className="text-gray-600 text-sm whitespace-pre-wrap mt-1">{a.body}</p>}
                        <p className="text-xs text-gray-400 mt-1">{a.activityDate ? new Date(a.activityDate).toLocaleString() : new Date(a.createdAt).toLocaleString()}</p>
                      </div>
                      <button onClick={() => { setEditingActivityId(a.id); setShowActivityForm(false); }} className="text-gray-400 hover:text-blue-600 shrink-0 self-start" title="Edit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No activity logged yet.</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
        </div>
        <div className="p-4 md:p-6">
          <form onSubmit={handleAddNote} className="flex flex-col sm:flex-row gap-2 mb-4">
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="submit" disabled={!newNote.trim()} className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 sm:self-end">Add</button>
          </form>
          {contact.notes && contact.notes.length > 0 ? (
            <div className="space-y-4">
              {contact.notes.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((n) => (
                <div key={n.id} className="border-b pb-3 last:border-b-0">
                  <p className="text-gray-900 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No notes yet.</p>
          )}
        </div>
      </div>

      {/* Tasks */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
          <Link to={`/crm/tasks/new?contactId=${id}`} className="text-sm text-blue-600 hover:text-blue-800">+ Add task</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    <Link to={`/crm/tasks/${t.id}`} className="text-blue-600 hover:text-blue-800">{t.title}</Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{t.dueDate || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || ''}`}>{t.status.replace(/_/g, ' ')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasks.length === 0 && <div className="text-center py-12 text-gray-500">No tasks for this contact.</div>}
        </div>
      </div>

      {/* Subtasks assigned to this contact */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Assigned Subtasks</h2>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={showCompletedSubtasks} onChange={(e) => setShowCompletedSubtasks(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
            Show completed
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtask</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Done</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assignedSubtasks.map((s) => {
                const overdue = !s.completed && s.dueDate && s.dueDate < new Date().toLocaleDateString('en-CA');
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className={`px-6 py-4 ${s.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{s.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/crm/tasks/${s.taskId}`} className="text-blue-600 hover:text-blue-800">{s.taskTitle}</Link>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${overdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{s.dueDate || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{s.completed ? 'Yes' : 'No'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {assignedSubtasks.length === 0 && (
            <div className="text-center py-12 text-gray-500">{showCompletedSubtasks ? 'No subtasks assigned to this contact.' : 'No open subtasks assigned to this contact.'}</div>
          )}
        </div>
      </div>

      {/* Deals */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Associated Deals</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deals.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    <Link to={`/crm/deals/${d.id}`} className="text-blue-600 hover:text-blue-800">{d.name}</Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-semibold">{d.value != null ? `$${d.value.toLocaleString()}` : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">{d.stage.replace(/_/g, ' ')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {deals.length === 0 && <div className="text-center py-12 text-gray-500">No deals associated with this contact.</div>}
        </div>
      </div>

      <ConfirmModal open={deleteModal} title="Delete Contact" message="Are you sure you want to delete this contact?" onConfirm={handleDelete} onCancel={() => setDeleteModal(false)} />
    </Layout>
  );
}
